import type { AttributeTileProps, FlagColor, FlagPattern } from '../types/game';
import { 
  Columns3, 
  Rows3, 
  Plus, 
  Circle, 
  Star, 
  Shapes, 
  Diamond, 
  Triangle, 
  Layers,
  TrendingUp,
  Tally5
} from 'lucide-react';

const colorStyles: Record<FlagColor, string> = {
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  white: 'bg-white border border-gray-400',
  green: 'bg-green-500',
  yellow: 'bg-yellow-400',
  black: 'bg-black',
  orange: 'bg-orange-500'
};

const patternIcons: Record<FlagPattern, React.ComponentType<any>> = {
  'mixed-stripes': Tally5,
  'horizontal-stripes': Rows3,
  'vertical-stripes': Columns3,
  'cross': Plus,
  'circle': Circle,
  'stars': Star,
  'symbol': Shapes,
  'diamond': Diamond,
  'triangle': Triangle,
  'geometric': Layers,
  'diagonal': TrendingUp
};

export const AttributeTile = ({ attribute, animationDelay = 0, isCurrentGuess = false, isFutureRow = false }: AttributeTileProps) => {
  const getStateClasses = () => {
    // If it's a future row, make it grayed out regardless of state
    if (isFutureRow) {
      return 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500';
    }

    switch (attribute.state) {
      case 'correct':
        return 'bg-emerald-600 text-white border-emerald-700';
      case 'present':
        return 'bg-amber-600 text-white border-amber-700';
      case 'absent':
        return 'bg-gray-400 text-white border-gray-500';
      case 'filled':
        return 'bg-blue-100 border-blue-300 text-gray-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-500';
    }
  };

  const getContent = () => {
    if (!attribute.value) {
      return (
        <div className="text-xs opacity-60">
          {attribute.type === 'pattern' ? 'Pattern' : 
           attribute.type === 'primaryColor' ? '1st' :
           attribute.type === 'secondaryColor' ? '2nd' : '3rd'}
        </div>
      );
    }

    if (attribute.type === 'pattern') {
      const PatternIcon = patternIcons[attribute.value as FlagPattern];
      return (
        <div className="flex items-center justify-center w-full h-full">
          <PatternIcon size={20} />
        </div>
      );
    } else {
      // Color attribute
      return (
        <div className="flex items-center justify-center w-full h-full">
          <div 
            className={`w-8 h-8 rounded-full ${colorStyles[attribute.value as FlagColor]} ${
              attribute.value === 'white' ? 'border border-gray-400' : ''
            }`}
          />
        </div>
      );
    }
  };

  return (
    <div 
      className={`
        attribute-tile w-16 h-16 flex flex-col items-center justify-center rounded-lg border-2 transition-all duration-300
        ${getStateClasses()} 
        ${isCurrentGuess ? 'animate-pulse' : ''}
      `}
      style={{ 
        animationDelay: `${animationDelay}ms`,
        transform: attribute.state !== 'empty' && attribute.state !== 'filled' ? 'rotateY(0deg)' : undefined
      }}
    >
      {getContent()}
    </div>
  );
};